// Lib
import { createSelector } from 'reselect';

// Utils
import {
    getElementId,
    getLineEndConnectedElementId,
    getLineStartConnectedElementId,
} from '../../../common/elements/utils/elementPropertyUtils';
import { isLine } from '../../../common/elements/utils/elementTypeUtils';
import { isEmpty, length } from '../../../common/utils/immutableHelper';

// Selectors
import { getCurrentlyEditingId } from '../../element/selection/currentlyEditingSelector';
import { getIsCurrentVisibleBoardFetched } from '../../element/board/boardSelector';
import {
    currentBoardInboxElementIdsSelector,
    currentBoardCanvasElementIdsSelector,
    currentBoardCanvasElementsSelector,
} from '../../element/selectors/currentBoardSelector';
import { createCustomEqualShallowSelector, createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';
import { getSelectedElementIds } from '../../element/selection/selectedElementsSelector';
import { attachModeHoveredElementIdSelector, isAttachModeSelector } from '../../reducers/draggingSelector';
import { parentIdMapSelector } from '../../element/selectors/elementGraphSelector';
import { inboxOpenStateSelector } from '../../inbox/InboxContainer';

// Constants
import { InboxState } from '../../../common/boards/boardConstants';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { getIsSheetOpen } from '../../mobile/structural/sheet/sheetSelectors';
import { SheetId } from '../../mobile/structural/sheet/sheetTypes';
import { MilanoteApplicationMode } from '../../../common/platform/platformTypes';

// We want to ignore changes in the parentIdGraph, because they're not overly important and we don't
// want to run the selector that much
const elementSortArgumentEqualityChecker = (
    [prevElements, prevEditingId, prevHoveredElementId, prevSelectedElementIds],
    [nextElements, nextEditingId, nextHoveredElementId, nextSelectedElementIds],
) =>
    prevElements === nextElements &&
    prevEditingId === nextEditingId &&
    prevHoveredElementId === nextHoveredElementId &&
    prevSelectedElementIds === nextSelectedElementIds;

const createElementSortSelector = createCustomEqualShallowSelector(elementSortArgumentEqualityChecker);

// Add some z-index steps between each element, to allow connected elements to sit above them, without conflicting
// with the z-index of other elements
const Z_INDEX_SPACING = 10;
/**
 * Only using a create selector here because it's safe to assume we can use strict equality.
 * Whenever either one of the inputs change the output will certainly change.
 */
const elementSortIndexMapSelector = createElementSortSelector(
    currentBoardCanvasElementsSelector,
    getCurrentlyEditingId,
    attachModeHoveredElementIdSelector,
    getSelectedElementIds,
    parentIdMapSelector,
    (sortedCanvasElements, currentlyEditingId, attachModeHoveredElementId, selectedElementIds, parentIdMap) => {
        const elementCount = length(sortedCanvasElements);

        const alreadySortedElements = {};
        const forceUpLines = [];

        const sortIndexMap = sortedCanvasElements.reduce((acc, element, index) => {
            // Don't want to use 0, for the sake of simplifying the line force up logic.
            const zIndex = index + 1;

            const id = getElementId(element);
            alreadySortedElements[id] = true;

            const isElementALine = isLine(element);

            // Default z-index
            acc[id] = zIndex * Z_INDEX_SPACING;

            // Force selected lines to the top
            if (isElementALine && !isEmpty(selectedElementIds) && selectedElementIds.includes(id)) {
                acc[id] = zIndex * Z_INDEX_SPACING + elementCount * Z_INDEX_SPACING;
                // Force connected lines on top of elements they're connected to
            } else if (isElementALine) {
                const startSnappedElementId = getLineStartConnectedElementId(element);
                const endSnappedElementId = getLineEndConnectedElementId(element);

                // We need to force lines up that aren't already above their connected elements
                const shouldForceUp =
                    (startSnappedElementId && !alreadySortedElements[startSnappedElementId]) ||
                    (endSnappedElementId && !alreadySortedElements[endSnappedElementId]);

                if (shouldForceUp) {
                    forceUpLines.push(element);
                }
            }

            return acc;
        }, {});

        // If we have lines to force up - make sure they're above the highest connected element
        if (!isEmpty(forceUpLines)) {
            const nextZIndex = {};

            forceUpLines.forEach((line) => {
                const id = getElementId(line);

                const startSnappedElementId = getLineStartConnectedElementId(line);
                const startSnappedElementParentId = parentIdMap[startSnappedElementId];
                const endSnappedElementId = getLineEndConnectedElementId(line);
                const endSnappedElementParentId = parentIdMap[endSnappedElementId];

                const startElIndex =
                    sortIndexMap[startSnappedElementId] || sortIndexMap[startSnappedElementParentId] || 0;
                const endElIndex = sortIndexMap[endSnappedElementId] || sortIndexMap[endSnappedElementParentId] || 0;

                let zIndex;

                if (startElIndex > endElIndex) {
                    zIndex = nextZIndex[startSnappedElementId] || startElIndex + 1;
                    nextZIndex[startSnappedElementId] = zIndex + 1;
                } else {
                    zIndex = nextZIndex[endSnappedElementId] || endElIndex + 1;
                    nextZIndex[endSnappedElementId] = zIndex + 1;
                }

                sortIndexMap[id] = zIndex;
            });
        }

        if (currentlyEditingId || attachModeHoveredElementId) {
            const id = currentlyEditingId || attachModeHoveredElementId;
            // Just to be safe add 10
            sortIndexMap[id] = elementCount * Z_INDEX_SPACING + sortIndexMap[id] + 10;
        }

        return sortIndexMap;
    },
);

const currentBoardCanvasElementIdsSortedById = createShallowSelector(
    currentBoardCanvasElementIdsSelector,
    (canvasElementIds) => canvasElementIds.sort(),
);

const emptyState = {
    inboxElementIds: [],
    canvasElementIds: [],
    sortedCanvasElementIds: [],
};

const getIsInboxOpen = createSelector(
    getPlatformDetailsSelector,
    inboxOpenStateSelector,
    getIsSheetOpen(SheetId.Inbox),
    (platformDetails, inboxState, isInboxSheetOpen) => {
        if (platformDetails.appMode === MilanoteApplicationMode.mobile) return isInboxSheetOpen;

        return inboxState === InboxState.OPEN;
    },
);

export default createSelector(
    getIsCurrentVisibleBoardFetched,
    currentBoardInboxElementIdsSelector,
    elementSortIndexMapSelector,
    // Sort canvasElementIds by their ID to prevent them from jumping around in the DOM when their ID changes.
    // This prevents unnecessary DOM manipulation
    currentBoardCanvasElementIdsSortedById,
    isAttachModeSelector,
    getIsInboxOpen,
    (currentBoardFetched, inboxElementIds, sortIndexMap, canvasElementIds, isAttachMode, isInboxOpen) => {
        if (!currentBoardFetched) return emptyState;

        return {
            inboxElementIds,
            canvasElementIds,
            sortIndexMap,
            isAttachMode,
            isInboxOpen,
        };
    },
);
